import {makeAutoObservable} from "mobx";
import SubscriptionPlanEditorStore from "./subscription-plan-editor-store";
import {toNullableNumber, toNumber} from "../../../models/model-types-methods";
import { IInstallment } from "../../../models/Plan";
import {IDateRangeISO} from "../../../components/date-range-picker-v2/date-range-picker-v2";
import {convertSqlDateTimeStringToDate} from "../../../utilities/timezone-utilities";

export interface IInstallmentChanges extends Partial<IInstallment> {}

class PlanInstallmentEditorStore {

    private _model?: IInstallment;
    private _changes: IInstallmentChanges = {};
    private _uncommittedChanges: IInstallmentChanges = {};
    private _isInEditMode: boolean = false;

    
    constructor(private _subscriptionPlanEditorStore: SubscriptionPlanEditorStore, model?: IInstallment) {
        makeAutoObservable(this);
        if(model) {
            this._model = ensureValidModelCopy(model);
        }
    }

    public getSubscriptionPlanEditorStore(): SubscriptionPlanEditorStore { return this._subscriptionPlanEditorStore; }

    public setDaysSinceLastInstalment(v: number): void { this._uncommittedChanges.days_since_last_installment = v; }
    public get daysSinceLastInstallment(): number | null | undefined { return this._uncommittedChanges.days_since_last_installment ?? this._changes?.days_since_last_installment ?? this._model?.days_since_last_installment; }
    public setInstallmentAmount(v: number): void { this._uncommittedChanges.charge_amount = v; }
    public get installmentAmount(): number|undefined { return this._uncommittedChanges.charge_amount ?? this._changes?.charge_amount ?? this._model?.charge_amount; }
    public get isDeleted(): boolean { return this._changes._deleted ?? false; }
    public get monthsSinceLastInstallment(): number | null | undefined { return this._uncommittedChanges.months_since_last_installment ?? this._changes?.months_since_last_installment ?? this._model?.months_since_last_installment; }
    public setMonthsSinceLastInstallment(v: number): void { this._uncommittedChanges.months_since_last_installment = v; }
    
    /**
     * Returns absolute charge date as Date object.
     * @returns {Date | null} Date object or null if not set.
     * @deprecated Use absoluteChargeDateISO for DateRangePickerV2. Kept for backward compatibility.
     */
    public get absoluteChargeDate(): Date | null {
        const absoluteChargeDateString = this._uncommittedChanges.absolute_charge_date ?? this._changes?.absolute_charge_date ?? this._model?.absolute_charge_date;
        if(!absoluteChargeDateString) {
            return null;
        }

        return convertSqlDateTimeStringToDate(absoluteChargeDateString)
    }

    /**
     * Returns absolute charge date as ISO UTC object for DateRangePickerV2.
     * @returns {IDateRangeISO | undefined} ISO UTC date range object (single day: 00:00:00 to 23:59:59 UTC) or undefined if not set.
     * @example { from: "2025-12-31T00:00:00.000Z", to: "2025-12-31T23:59:59.999Z" }
     */
    public get absoluteChargeDateISO(): IDateRangeISO | undefined {
        const dbDateString = this._uncommittedChanges.absolute_charge_date 
            ?? this._changes?.absolute_charge_date 
            ?? this._model?.absolute_charge_date;
        if (!dbDateString) {
            return undefined;
        }
        
        /** Convert DB format "2024-12-31 12:00:00" to ISO UTC single day range */
        const date = convertSqlDateTimeStringToDate(dbDateString);
        
        /** Create single day range in UTC (00:00:00 to 23:59:59) */
        const startOfDay = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));
        const endOfDay = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 999));
        
        return {
            from: startOfDay.toISOString(),
            to: endOfDay.toISOString()
        };
    }

    /**
     * Sets absolute charge date from ISO UTC object (DateRangePickerV2 format).
     * Converts ISO UTC to DB format "YYYY-MM-DD 12:00:00".
     * @param {IDateRangeISO | null | undefined} value - ISO UTC date range object, null, or undefined.
     * @example setInstallmentAbsoluteChargeDate({ from: "2025-12-31T00:00:00.000Z", to: "2025-12-31T23:59:59.999Z" })
     */
    public setInstallmentAbsoluteChargeDate(value: IDateRangeISO | null | undefined): void {
        if(value?.from) {
            /** Parse ISO UTC "from" date and convert to DB format */
            const date = new Date(value.from);
            this._uncommittedChanges.absolute_charge_date = dateToDbString(date);
        }
    }

    public setInstallmentDefaultDate(): void {
        const defaultDate = defaultInstallmentDate();
        /** Convert default date to DB format */
        this._uncommittedChanges.absolute_charge_date = dateToDbString(defaultDate);
    }

    public setDeleted(): void {
        this._changes._deleted = true;
    }

    public setInEditMode(v: boolean): void { 
        this._isInEditMode = v; 
    }
    
    public isInEditMode(): boolean { return this._isInEditMode; }

    public hasChanges(): boolean { return !!Object.keys(this._changes).length; }

    public setChanges(changes: IInstallmentChanges): void {
        this._changes = changes;
    }
    
    public get isImmediate(): boolean { 
        if (this.monthsSinceLastInstallment === 0) {
            return true;
        }
        if (this.daysSinceLastInstallment === 0) {
            return true;
        }
        return false;
    }

    public isMonthsInterval(): boolean {
        return isSet(this.monthsSinceLastInstallment);
    }

    public isAbsoluteInterval(): boolean {
        return isSet(this.absoluteChargeDate);
    }

    public hasServerModel(): boolean {
        return !!this._model?.id;
    }

    public commitChanges (): void {
        this._changes = { ...this._changes, ...this._uncommittedChanges } as IInstallmentChanges;
        this._uncommittedChanges = {};
        this.setInEditMode(false);
    }

    public discardUncommittedChanges(): void {
        this._uncommittedChanges = {};
        this.setInEditMode(false);
    }

    public getUpdatedModel(): IInstallment {
        return { ...this._model, ...this._changes } as IInstallment;
    }
}

export default PlanInstallmentEditorStore;

function ensureValidModelCopy(data: IInstallment): IInstallment {
    return {
        ...data,
        months_since_last_installment: toNullableNumber(data.months_since_last_installment),
        charge_amount: toNumber(data.charge_amount),
    };
}

/**
 * Checks if a value is set (not undefined and not null).
 * @param {any} v - Value to check.
 * @returns {boolean} True if value is set.
 */
function isSet(v?: any|null): boolean {
    return v !== undefined && v !== null;
}

/**
 * Converts Date object to DB format string.
 * Output: "YYYY-MM-DD 12:00:00" (UTC time, always 12:00:00).
 * @param {Date} date - Date object to convert.
 * @returns {string} Date string in DB format.
 * @example dateToDbString(new Date("2025-12-31")) // "2025-12-31 12:00:00"
 */
function dateToDbString(date: Date): string {
    const zeroPad = (v: number) => v.toString().padStart(2, '0');
    return `${date.getUTCFullYear()}-${zeroPad(date.getUTCMonth() + 1)}-${zeroPad(date.getUTCDate())} 12:00:00`;
}

/**
 * Generates default installment date (tomorrow at 12:00:00 UTC).
 * @returns {Date} Date object set to tomorrow at 12:00:00 UTC.
 */
function defaultInstallmentDate(): Date {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 12, 0, 0));
}